import {useRef, useState} from 'react'
import {Button} from '@primer/react'
import type {PullRequestUserActionStack} from '@github-ui/pull-requests/analytics/pull-request-user-action'

import {RebaseStackConfirmationDialog} from '../../../common/RebaseStackConfirmationDialog'
import {ResolveConflictsDialog} from '../../../common/ResolveConflictsDialog'
import {useRebaseStack} from '../../../../../hooks/use-rebase-stack'
import type {StackSectionStackEntry} from '../../../../../helpers/merge-box-status-calculator/stack-section-status'

/**
 * CTA that starts a stack rebase. It owns the rebase flow via the shared `useRebaseStack`
 * hook (analytics, mutation, and out-of-date count).
 *
 * Renders nothing when the viewer can't rebase (no push access, blocked by an authorization
 * policy, or the stack is collapsed to just its list). When the base branch requires signed
 * commits, the rebase must happen on the command line, so it opens the shared CLI
 * instructions dialog instead of the in-app confirmation dialog.
 */
export function RebaseStackButton({
  stackEntries,
  stackMetadata,
  canRebase,
  stackBaseRefRequiresSignatures,
}: {
  stackEntries: StackSectionStackEntry[]
  stackMetadata?: PullRequestUserActionStack
  canRebase: boolean
  stackBaseRefRequiresSignatures: boolean
}) {
  const [showDialog, setShowDialog] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const {onRebaseStack, outOfDateCount, isRebasing} = useRebaseStack({stackEntries, stackMetadata})

  if (!canRebase) return null

  const closeDialog = () => {
    setShowDialog(false)
    buttonRef.current?.focus()
  }

  return (
    <>
      <Button
        ref={buttonRef}
        onClick={() => setShowDialog(true)}
        loading={isRebasing}
        loadingAnnouncement="Rebasing stack"
      >
        Rebase stack
      </Button>
      {showDialog && stackBaseRefRequiresSignatures && (
        <ResolveConflictsDialog
          stackNumber={stackMetadata?.number ?? 0}
          returnFocusRef={buttonRef}
          onClose={closeDialog}
        />
      )}
      {showDialog && !stackBaseRefRequiresSignatures && (
        <RebaseStackConfirmationDialog
          outOfDateCount={outOfDateCount}
          onConfirm={onRebaseStack}
          onClose={closeDialog}
        />
      )}
    </>
  )
}
